import Event from '../../utils/event';
import BaseService from '../../base/base_service';
import {SYSTEM_EVENTS} from '../../events/events';
import * as EXTERNAL_LINKS_CONSTS from '../../components/external_links/external_links_consts';
import {GNWP_SOURCES} from '../../components/external_links/external_links_consts';
import _ from '../../utils/underscore';
import {PRIORITY_LEVEL} from '../overlay_manager/overlay_manager_levels'
import Store from "../../utils/store";

const UCC_PERSISTANT_KEY = 'ucc';

class OmgService extends BaseService {

    /**
     * @static
     * @type {string}
     */
    static CLASS = 'OmgService';

    /**
     * Url for GetAwaitingMessages API
     * @type {string}
     * @private
     */
    _uccURL = '/cgpapi/omg/GetAwaitingMessages';
    _ignorePopopURLs = [];
    _UCCMessages = [];
    _currentNumberOfMessages = 0;


    /**
     *
     * @param params
     */
    init(params) {
        // init params
        this._uccURL = (params && params.getAwaitingMessagesUrl) || this._uccURL;
        this._setIgnorePopupURLs(params && params.ignorePopopURLs);

        this._subscribe();
    }



    /**
     *
     * @public
     */
    start() {
        this._subscribe();
    }

    onInitializationCompleted() {
        return this._getAwaitingMessages();
    }

    _setIgnorePopupURLs(ignorePopopURLs = []) {
        let pathsList = [];
        if (_.isString(ignorePopopURLs)) {
            pathsList = _.safeJSON(ignorePopopURLs, this.ignorePopopURLs);
        } else if(_.isArray(ignorePopopURLs)) {
            pathsList = ignorePopopURLs;
        }

        this._ignorePopopURLs = pathsList;
        return this;
    }

    /**
     * Subscribe application events.
     *
     * @private
     */
    _subscribe() {
        this.onSystemEvent(SYSTEM_EVENTS.SESSION_EVENT_OMG_RECEIVED, this._openOMG, this);
        this.onSystemEvent(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, this._onOAuthStateChanged, this);
        this.onSystemEvent(SYSTEM_EVENTS.DEPOSIT_WIDGET_CLOSED, this._onDepositWidgetClosed)
        this.onSystemEvent(SYSTEM_EVENTS.EXTERNAL_LINK_CLOSED, (linkSource) => {
            this._onExternalLinkClosed(linkSource);
        });
        this.onSystemEvent(SYSTEM_EVENTS.OMG_OPEN_AWAITING_MESSAGES, () => {
           this._openUCCMessages();
        });
        this.onSystemEvent(SYSTEM_EVENTS.EXTERNAL_LINK_OPENED, (source) => {
            if (source === GNWP_SOURCES.OMG) {
               this._onOMGOpenedHandler();
            }
        });

    }

    _onOAuthStateChanged() {
        if(!this.serviceFactory.oAuth2Service().isAuthorized()) {
            this._notifyOnUCCChanges(0);
            return;
        }

        this._getAwaitingMessages().then(() => {
            this._openUCCMessages();
        });
    }


    /**
     * Validates whether current url path  is under one of the folders that are in the ignore list urls
     *  @example if current path is /promotion/game/  and /promotion/ is part of the ignore list - will return true (is ignore path)
     * @returns {boolean}
     * @private
     */
    _isPathIgnored()  {
        let currentPath =  this.serviceFactory.windowService().getPathName();

        let  ignoreUrlsArray = this._ignorePopopURLs.filter((ignoreUrl)=>{
            return currentPath.startsWith(ignoreUrl)
        });

        return ignoreUrlsArray.length  > 0;
    }


    /**
     * decision function for showing OMG
     * @param OMGData
     * @private
     */
    _getOmgOpenOptions(OMGData) {
        let gameService = this.serviceFactory.gameService();
        this.logger.debug(`is game running flag is: ${gameService.isGameRunning()}`);

        if (this._isPathIgnored()) {
            this.logger.debug('open OMG is triggered but the user is on an ignored pathname, skipping');
            return;
        } else
        if ((
            gameService.isGameRunning() ||
            this.serviceFactory.overlayManagerService().isCashierOpen() ||
            this.serviceFactory.overlayManagerService().isMembershipOpen()) &&
            '0' === OMGData.OpenedWithinGame) {
            this.logger.debug('open OMG is triggered but game is running & OpenedWithinGame omg property is false so open OMG behind');
            return {priorityLevel: PRIORITY_LEVEL.OMG_BEHIND};
        } else
        if (!this.serviceFactory.visibilityDetectorService().isActive()) {
            this.logger.debug('open OMG is triggered but tab isnt active so aborting OMG process');
            return;
        }

         return {priorityLevel: PRIORITY_LEVEL.OMG};
    }


    /**
     * open omg messages through web platform gnwp
     * @param OMGData
     * @private
     */
    _openOMG(OMGData) {
        let omgOptions = this._getOmgOpenOptions(OMGData);
        if (!omgOptions) {
            return;
        }

        Event.fire(SYSTEM_EVENTS.OMG_DISPLAYED, OMGData);

        this._openOMGMessage(OMGData, omgOptions);
    }


    /**
     * Open OMG messages in External link
     * @param OMGData
     */
    _openOMGMessage(messages, options) {
        this.serviceFactory.openLinkService().openLink({
            source: EXTERNAL_LINKS_CONSTS.GNWP_SOURCES.OMG,
            view: 'iframe',
            priorityLevel: options && options.priorityLevel,
            /*            TODO ExternalLinkInPopUp param needs to be populated dynamically and not hard-coded to '1'.
             ( '1' - external link will be opened by we b team from omg iframe , '0' - cgp will get post msg )*/
            options: Object.assign({ExternalLinkInPopUp: '1'}, messages)
        });
    }


    /**
     * Open awaiting messages
     * @private
     */
    _openUCCMessages() {
        let omgMessages = this._UCCMessages;
        let xmlFormattedMessages = this._convertToXMLMessages(omgMessages);
        if (xmlFormattedMessages) {
            this._openOMG({Messages: xmlFormattedMessages, OpenedWithinGame: '0'});
        }
    }


    /**
     * Update awaiting messages state after opening OMG link
     * @private
     */
    _onOMGOpenedHandler() {
        this._saveUCCMessages(); // when external link open
        if (this._UCCMessages) {
            this._notifyOnUCCChanges(this._UCCMessages.length);
        }
    }


    /**
     * Get awaiting OMG messages from UCC Service
     * @private
     */
    _getAwaitingMessages() {
        if (!this.serviceFactory.oAuth2Service().isAuthorized()) {
            this._notifyOnUCCChanges(0);
            return Promise.resolve();
        }

        return this.serviceFactory.http2Service().post(this._uccURL)
            .then((response) => {
                if (response && response.MessagesJSON) {
                    this._onUCCMessagesReceived(response.MessagesJSON);
                }

                return response;
            })
            .catch((error) => {
                this.logger.error(this);
                throw error;
            });
    }


    /**
     *
     * @param messages
     * @private
     */
    _onUCCMessagesReceived(messages) {
        this.logger.debug('UCC messages received: ' + JSON.stringify(messages));
        let displayedMessages = this._getDisplayedUCCMessages();
        // in case there is only single message, server converts it to object and not messages array
        if (!messages) {
            return;
        }
        if (false === _.isArray(messages)) {
            let messageArray = [];
            messageArray.push(messages);
            messages = messageArray;
        }
        let newNumberOfMessages = this._getCurrentNumberOfMessagesByComparison(messages, displayedMessages);
        this.logger.debug('New number of awaiting messages after comparison = ' + newNumberOfMessages);
        this._UCCMessages = messages;
        this._notifyOnUCCChanges(newNumberOfMessages);
    }


    /**
     * Fire event on any state change in number of awaiting messages
     * @private
     */
    _notifyOnUCCChanges(updatedNumberOfMessages) {
        this._currentNumberOfMessages = updatedNumberOfMessages;
        Event.fire(SYSTEM_EVENTS.OMG_AWAITING_MESSAGES_UPDATED, updatedNumberOfMessages);
    }


    /**
     * Compare received UCC messages with already read and get the number of awaiting messages
     * @param receivedMessages
     * @param readMessages
     * @returns {*}
     * @private
     */
    _getCurrentNumberOfMessagesByComparison(receivedMessages, readMessages) {
        if (0 === receivedMessages.length || false === this.serviceFactory.oAuth2Service().isAuthorized()) {
            this.logger.debug('There are no received messages or user is not authorized. Change current number of messages to 0');
            return 0;
        }

        if (!readMessages || 0 === readMessages.length) {
            this.logger.debug('There are no read messages. Change state to number of received messages');
            return receivedMessages.length;
        }

        let newCompareSet = receivedMessages.slice(0, receivedMessages.length > 3 ? 3 : receivedMessages.length);
        let existCompareSet = readMessages.slice(0, readMessages.length > 3 ? 3 : readMessages.length);

        let newMessages = newCompareSet.filter((newMessage) => {
            let isExist = false;

            for (let i = 0, len = existCompareSet.length; i < len; i++) {
                let existMessage = existCompareSet[i];
                if (newMessage['@id'] === existMessage['@id']) {
                    isExist = true;
                    break;
                }
            }

            return !isExist;
        });

        this.logger.debug('Comparison between messages finished. Received Messages ' + JSON.stringify(receivedMessages));
        this.logger.debug('Comparison between messages finished. Read Messages ' + JSON.stringify(readMessages));
        this.logger.debug('Comparison between messages finished. New messages ' + JSON.stringify(newMessages));
        return newMessages.length > 0 ? newMessages.length : receivedMessages.length;
    }

    /**
     * Convert JSON messages to XML format for OMG
     * @param messages
     * @returns {string}
     * @private
     */
    _convertToXMLMessages(messages) {
        if (!_.isArray(messages) || messages.length === 0) {
            return;
        }
        let rootNode = '<Messages>';
        messages.forEach((message) => {
            rootNode += this._createXMLMessage(message);
        });
        rootNode += '</Messages>';

        return rootNode;
    }


    /**
     * Create JSON message to XML
     * @param jsonMessage
     * @returns {string}
     * @private
     */
    _createXMLMessage(jsonMessage) {
        let messageNode = '<Message ';
        let xmlAttrs = [];
        Object.keys(jsonMessage).forEach((key) => {
            xmlAttrs.push(this._createXMLAtribute(key.replace('@', ''), jsonMessage[key]));
        });
        messageNode += xmlAttrs.join(' ') + '></Message>';
        return messageNode;
    }

    /**
     * Create xml attribute
     * @param attrName
     * @param attrValue
     * @returns {string}
     * @private
     */
    _createXMLAtribute(attrName, attrValue) {
        return '' + attrName + '="' + attrValue + '"';
    }


    /**
     * Handle close of external links
     * In case OMG, MyAccount or cashier closed, we need to udpate awaiting messages
     * @param linkSource
     * @private
     */
    _onExternalLinkClosed(linkSource) {
        if (GNWP_SOURCES.CASHIER === linkSource || GNWP_SOURCES.MY_ACCOUNT === linkSource || GNWP_SOURCES.OMG === linkSource) {
            this._getAwaitingMessages();
        }
    }

    _onDepositWidgetClosed = () => {
        this._getAwaitingMessages();
    }

    /**
     * Save new UCC messages to local storage
     * @param messages
     */
    _saveUCCMessages() {
        Store.set(UCC_PERSISTANT_KEY, JSON.stringify(this._UCCMessages));
    }


    _getDisplayedUCCMessages() {
        return _.safeJSON(Store.get(UCC_PERSISTANT_KEY));
    }


    getNumberOfAwaitingMessages() {
        return this._currentNumberOfMessages;
    }
}

export default OmgService;